import { AsYouType, parsePhoneNumberFromString, validatePhoneNumberLength } from 'libphonenumber-js/max';

const onlyDigits = (value) => String(value ?? '').replace(/\D/g, '');

// "+90" + "5555555555" -> "+905555555555". Numara zaten "+" ile geliyorsa kod eklenmez.
const toE164Candidate = (phone, phoneCode) => {
  const raw = String(phone ?? '').trim();
  if (raw.startsWith('+')) return `+${onlyDigits(raw)}`;
  const code = onlyDigits(phoneCode);
  return code ? `+${code}${onlyDigits(raw)}` : '';
};

// Biçimli metinden seçilen ülke kodunu hane sayarak atar. CountryList'te bazı kodlar
// alan koduyla birleşik ("+1242" Bahamalar) ve kütüphane bunları "+1 242 ..." diye
// böldüğü için düz "startsWith" yetmiyor.
const stripDialCode = (formatted, code) => {
  let remaining = code.length;
  let index = 0;
  while (index < formatted.length && remaining > 0) {
    if (/\d/.test(formatted[index])) remaining -= 1;
    index += 1;
  }
  return remaining === 0 ? formatted.slice(index).trim() : '';
};

export const parsePhone = (phone, phoneCode) => {
  const candidate = toE164Candidate(phone, phoneCode);
  return candidate ? parsePhoneNumberFromString(candidate) : undefined;
};

/**
 * Input için yazdıkça biçimlendirme: ülke kodu ayrı alanda seçildiğinden
 * yalnızca numara kısmı döner. "5555555555" (+90) -> "555 555 55 55"
 */
export function formatPhoneInput(phone, phoneCode) {
  const digits = onlyDigits(phone);
  const code = onlyDigits(phoneCode);
  if (!digits || !code) return digits;

  const formatted = new AsYouType().input(`+${code}${digits}`);
  return stripDialCode(formatted, code) || digits;
}

// E.164'te ülke kodu hariç numara en fazla 15 hane olabiliyor.
const MAX_PHONE_DIGITS = 15;

/**
 * Input'a yazılan yeni değeri sınırlar:
 * - Numara ülkenin izin verdiği en uzun numaradan uzunsa kabul edilmez
 *   (+229'da 10, +1'de 10 hane). Numara hatalı başlasa da sınır işler.
 * - Numara geçerli hale geldiyse, fazladan rakam ancak numara yine geçerli
 *   kalıyorsa kabul edilir. Türkiye'de kütüphane "fazla uzun" demediği için
 *   +90'da 555 555 55 55'ten sonrasını bu kural durdurur.
 */
export function limitPhoneInput(nextValue, prevDigits, phoneCode) {
  // "+90 555 555 55 55" yapıştırılırsa ülke kodu ayrı alanda olduğu için atılır.
  const raw = String(nextValue ?? '').trim();
  const code = onlyDigits(phoneCode);
  const pasted = raw.startsWith('+') && code && onlyDigits(raw).startsWith(code)
    ? onlyDigits(raw).slice(code.length)
    : raw;
  let next = onlyDigits(pasted).slice(0, MAX_PHONE_DIGITS);
  const prev = onlyDigits(prevDigits);
  if (next.length <= prev.length) return next;

  // Fazla uzun yapıştırılan numara reddedilmez, sığacak kadarı kalır.
  const isTooLong = (digits) =>
    Boolean(onlyDigits(phoneCode)) && validatePhoneNumberLength(toE164Candidate(digits, phoneCode)) === 'TOO_LONG';
  while (next.length > prev.length && isTooLong(next)) next = next.slice(0, -1);
  if (next.length <= prev.length) return prev;

  const prevParsed = parsePhone(prev, phoneCode);
  if (!prevParsed || !prevParsed.isValid()) return next;

  const nextParsed = parsePhone(next, phoneCode);
  return nextParsed && nextParsed.isValid() ? next : prev;
}

/**
 * Gösterim için: "+90 555 555 55 55". Çözülemeyen numara olduğu gibi gösterilir.
 */
export function formatPhoneDisplay(phone, phoneCode) {
  if (!phone) return '';
  const parsed = parsePhone(phone, phoneCode);
  if (parsed && parsed.isValid()) return parsed.formatInternational();
  const raw = String(phone).trim();
  return raw.startsWith('+') || !phoneCode ? raw : `${phoneCode} ${raw}`;
}

/**
 * Servise gidecek numara: yalnızca rakam, ülke kodu ve baştaki 0 olmadan.
 * "0555 555 55 55" (+90) -> "5555555555"
 */
export function toNationalPhoneDigits(phone, phoneCode) {
  const parsed = parsePhone(phone, phoneCode);
  const codePrefix = `+${onlyDigits(phoneCode)}`;
  // nationalNumber yerine seçilen koda göre kesiyoruz: ülke kodu servise ayrıca
  // gidiyor ve "+1242" gibi birleşik kodlarda ikisi birleşince numara bozulmamalı.
  if (parsed && parsed.isValid() && parsed.number.startsWith(codePrefix)) {
    return parsed.number.slice(codePrefix.length);
  }
  return onlyDigits(phone);
}
